/**
 * ``usePrefersReducedMotion`` — reflect the OS/browser reduced-motion setting
 * (Flow L §9; ADR-035 / S6.6).
 *
 * Reads ``(prefers-reduced-motion: reduce)`` and stays reactive to changes.
 * When true, the System Status live-motion treatments (stoplight-transition
 * pulse, connection-burst highlight, sustained-failover pulse) degrade to
 * instant state changes — the state is still conveyed by shape + text label +
 * ``aria-live`` (REQ-UI-046 / REQ-UI-059). Falls back to ``false`` (motion
 * allowed) when ``matchMedia`` is unavailable (SSR / older jsdom).
 *
 * Cross-references:
 *   - Flow L §9 (interaction & look-and-feel); REQ-UI-059 (a11y).
 *   - ADR-022 (theme system also honors a media query; same pattern).
 */
import { useEffect, useState } from "react";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** True when the user has requested reduced motion. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (): void => setReduced(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return reduced;
}
